/**
 * Modern Typing Speed Classifications Section (CPM-based)
 * Uses your color variables and animal images. Responsive and visually rich.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/TypingSpeedClassifications.css';
import snailImg from '../assets/snail.jpg';
import turtleImg from '../assets/turtle.jpg';
import rabbitImg from '../assets/rabbit.jpg';
import wolfImg from '../assets/wolf.jpg';
import cheetahImg from '../assets/cheetah.jpg';
import falconImg from '../assets/bird.jpg';

const typingLevels = [
  {
    name: 'Snail',
    value: '0-100 CPM',
    level: 'Beginner',
    description: 'The methodical typist who prioritizes accuracy over speed. Every keystroke is deliberate and carefully placed, creating a steady rhythm of progress.',
    img: snailImg,
    percent: 15
  },
  {
    name: 'Turtle',
    value: '101-200 CPM',
    level: 'Novice',
    description: 'Slow but steady wins the race. Turtle typists maintain consistent accuracy while gradually building speed, creating reliable and precise text input.',
    img: turtleImg,
    percent: 30
  },
  {
    name: 'Rabbit',
    value: '201-300 CPM',
    level: 'Intermediate',
    description: 'Quick bursts of speed with occasional pauses. Rabbit typists demonstrate good keyboard knowledge but may need to work on consistency to reach higher levels.',
    img: rabbitImg,
    percent: 50
  },
  {
    name: 'Wolf',
    value: '301-400 CPM',
    level: 'Advanced',
    description: 'Precise and swift, wolf typists have mastered the keyboard with excellent finger positioning and minimal errors, allowing them to soar through text.',
    img: wolfImg,
    percent: 70
  },
  {
    name: 'Cheetah',
    value: '401-500 CPM',
    level: 'Expert',
    description: 'Lightning-fast with remarkable accuracy, cheetah typists represent the elite of keyboard warriors, able to maintain incredible speeds with minimal effort.',
    img: cheetahImg,
    percent: 90
  },
  {
    name: 'Falcon',
    value: '501+ CPM',
    level: 'Legendary',
    description: 'Beyond natural limits, falcon typists seem to defy physics with their supernatural typing speeds, achieving rates that blur the line between human and machine.',
    img: falconImg,
    percent: 100
  }
];

const clickLevels = [
  {
    name: 'Snail',
    value: '0-3.99 CPS',
    level: 'Beginner',
    description: 'Slow and steady... but you can improve! Keep practicing to boost your click speed.',
    img: snailImg,
    percent: 15
  },
  {
    name: 'Turtle',
    value: '4-6.99 CPS',
    level: 'Novice',
    description: 'Steady and reliable clicking. You have good control, but there\'s room to go faster!',
    img: turtleImg,
    percent: 30
  },
  {
    name: 'Rabbit',
    value: '7-9.99 CPS',
    level: 'Intermediate',
    description: 'Quick and nimble clicking. You\'re above average—keep pushing for the top ranks!',
    img: rabbitImg,
    percent: 50
  },
  {
    name: 'Wolf',
    value: '10-11.99 CPS',
    level: 'Advanced',
    description: 'Incredible reflexes and speed. You\'re a natural at click speed tests!',
    img: wolfImg,
    percent: 70
  },
  {
    name: 'Cheetah',
    value: '12+ CPS',
    level: 'Expert',
    description: 'Lightning fast! Top 1% of clickers. Your reflexes are off the charts!',
    img: cheetahImg,
    percent: 90
  },
  {
    name: 'Falcon',
    value: '15+ CPS',
    level: 'Legendary',
    description: 'Falcon-level clickers are nearly superhuman. You\'ve reached the pinnacle of click speed!',
    img: falconImg,
    percent: 100
  }
];

export default function TypingSpeedClassifications({ mode = 'typing' }) {
  const isClick = mode === 'click';
  const levels = isClick ? clickLevels : typingLevels;
  return (
    <section className="typing-speed-section">
      <h2 className="typing-speed-title">
        <span className="accent">{isClick ? 'Click Speed' : 'Typing Speed'}</span> Classifications
        <div className="typing-speed-underline" />
      </h2>
      <p className="typing-speed-subtitle">
        {isClick
          ? 'See how your CPS (Clicks Per Second) stacks up! From the steady snail to the lightning-fast cheetah, find your clicker persona.'
          : 'See if you type like a Snail, Turtle, Rabbit, Wolf, or Cheetah. These ranks use CPM from the typing test — they are not a CPS click test.'}
      </p>
      <div className="typing-speed-grid">
        {levels.map((level) => (
          <div className="typing-speed-card" key={level.name}>
            <div className="typing-speed-img-wrap">
              <img
                className="typing-speed-img"
                src={level.img}
                alt={`${level.name} rank for ${isClick ? 'click' : 'typing'} speed: ${level.level}, ${level.value}`}
                width="512"
                height="512"
                loading="lazy"
                decoding="async"
              />
              <div className="typing-speed-img-gradient" />
            </div>
            <div className="typing-speed-card-content">
              <div className="typing-speed-card-header">
                <h3 className="typing-speed-card-name">{level.name}</h3>
                <span className="typing-speed-card-badge">{level.level}</span>
              </div>
              <div className="typing-speed-card-cpm">{level.value}</div>
              <div className="typing-speed-card-bar-bg">
                <div className="typing-speed-card-bar" style={{ width: `${level.percent}%` }} />
              </div>
              <div className="typing-speed-card-desc">{level.description}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="typing-speed-cta">
        <h3 className="cta-title">{isClick ? 'Ready to Test Your Click Speed?' : 'Ready to Test Your Typing Speed?'}</h3>
        <p className="cta-desc">
          {isClick
            ? 'Discover which animal represents your click speed. Take our click test now and see where you rank on the speed spectrum.'
            : 'Discover which animal represents your typing prowess. Take our advanced typing test now and see where you rank on the speed spectrum.'}
        </p>
        <Link className="cta-btn" to={isClick ? '/5s-cps-click-test' : '/60s-cpm-typing-test'}>
          {isClick ? 'Start Click Test' : 'Start Typing Test'}
        </Link>
      </div>
    </section>
  );
} 